/**
 * BaseGameScene — Generic Phaser scene driven by a SceneManifest.
 *
 * Reads background_layers, entity_visuals, hud_elements, and camera_options
 * from the manifest and draws them.  Domain-specific scenes (FootballScene)
 * extend this and add domain overlays.
 *
 * Uses a "pull" handshake with PhaserGame.js:
 *   1. create() registers EventBus listeners
 *   2. create() emits 'scene-ready' to tell React the scene is live
 *   3. PhaserGame hears 'scene-ready' and pushes current manifest/actors/frame
 *   4. Subsequent updates arrive via the same EventBus channels
 */

import Phaser from 'phaser';
import EventBus from '../EventBus';

export default class BaseGameScene extends Phaser.Scene {
  constructor(key = 'BaseGameScene') {
    super({ key });
    this.manifest = null;
    this.actorSprites = {};
    this.actorLabels = {};
    this.actors = [];
    this.currentCamera = 'top_down';
    this._ready = false;
    // 2026-06 — orientation flag flows in from React via the
    // 'set-orientation' EventBus channel. Defaults to landscape so
    // legacy 2D-only scenes (mounted before the React provider sends
    // an event) keep their previous behaviour.
    this._orientation = 'landscape';
  }

  create() {
    this._onManifest = (m) => this.applyManifest(m);
    this._onActors = (a) => this.setActors(a);
    this._onFrame = (f) => this.updateFrame(f);
    this._onCamera = (id) => this.switchCamera(id);
    this._onOrientation = (o) => this.setOrientation(o);

    EventBus.on('set-manifest', this._onManifest);
    EventBus.on('set-actors', this._onActors);
    EventBus.on('update-frame', this._onFrame);
    EventBus.on('switch-camera', this._onCamera);
    EventBus.on('set-orientation', this._onOrientation);

    this._ready = true;
    EventBus.emit('scene-ready');
  }

  destroy() {
    if (this._onManifest) EventBus.off('set-manifest', this._onManifest);
    if (this._onActors) EventBus.off('set-actors', this._onActors);
    if (this._onFrame) EventBus.off('update-frame', this._onFrame);
    if (this._onCamera) EventBus.off('switch-camera', this._onCamera);
    if (this._onOrientation) EventBus.off('set-orientation', this._onOrientation);
  }

  setOrientation(orientation) {
    const next = (orientation === 'portrait' || orientation === 'landscape') ? orientation : 'landscape';
    if (next === this._orientation) return;
    this._orientation = next;
    // Re-draw the background to pick up the new field orientation, then
    // re-place every actor sprite via setActors so labels / sprites
    // settle on their new screen coords without a full scene rebuild.
    if (this.manifest) {
      this.drawBackground();
      if (this.actors && this.actors.length > 0 && typeof this.setActors === 'function') {
        this.setActors(this.actors);
      }
    }
  }

  applyManifest(manifest) {
    this.manifest = manifest;
    this.drawBackground();
  }

  drawBackground() {
    if (!this.manifest) return;
    const { width, height } = this.scale;
    const layers = this.manifest.background_layers || [];

    layers.forEach((layer) => {
      if (layer.layer_type === 'solid') {
        const bg = this.add.rectangle(
          width / 2, height / 2, width, height,
          parseInt((layer.config.color || '#333333').replace('#', ''), 16)
        );
        bg.setDepth(-10);
      }
    });
  }

  setActors(actors) {
    // De-stack actors sharing the same position.
    const posMap = {};
    actors.forEach((actor) => {
      const pos = actor.initial_state?.position || { x: 0, y: 0 };
      const key = `${Math.round(pos.x * 10)}_${Math.round(pos.y * 10)}`;
      (posMap[key] = posMap[key] || []).push(actor);
    });
    Object.values(posMap).forEach((group) => {
      if (group.length < 2) return;
      const spread = 1.5;
      const startOffset = -((group.length - 1) * spread) / 2;
      group.forEach((actor, i) => {
        if (!actor.initial_state) actor.initial_state = {};
        if (!actor.initial_state.position) actor.initial_state.position = { x: 0, y: 0 };
        actor.initial_state.position.y += startOffset + i * spread;
      });
    });
    this.actors = actors;
    this.createActorSprites();
  }

  createActorSprites() {
    Object.values(this.actorSprites).forEach((s) => s.destroy());
    Object.values(this.actorLabels).forEach((l) => l.destroy());
    this.actorSprites = {};
    this.actorLabels = {};

    if (!this.manifest) return;
    const entityVisuals = this.manifest.entity_visuals || {};

    this.actors.forEach((actor) => {
      const visual = entityVisuals[actor.team] || entityVisuals.default || {};
      const pos = this.worldToScreen(
        actor.initial_state?.position?.x || 0,
        actor.initial_state?.position?.y || 0
      );

      const radius = visual.radius || 14;
      const fillColor = parseInt((visual.color || '#ffffff').replace('#', ''), 16);
      const borderColor = parseInt((visual.border_color || '#ffffff').replace('#', ''), 16);

      const circle = this.add.circle(pos.x, pos.y, radius, fillColor);
      circle.setStrokeStyle(visual.border_width || 2, borderColor);
      circle.setDepth(10);
      this.actorSprites[actor.actor_id] = circle;

      const label = this.add.text(pos.x, pos.y, actor.label || '', {
        fontSize: `${visual.label_font_size || 11}px`,
        fontFamily: 'Arial',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      label.setOrigin(0.5);
      label.setDepth(11);
      this.actorLabels[actor.actor_id] = label;
    });
  }

  updateFrame(frame) {
    if (!frame || !frame.actor_positions) return;
    const tweenMs = this.manifest?.animation_config?.tween_duration_ms || 400;

    Object.entries(frame.actor_positions).forEach(([actorId, state]) => {
      const sprite = this.actorSprites[actorId];
      const label = this.actorLabels[actorId];
      if (!sprite || !state?.position) return;

      const pos = this.worldToScreen(state.position.x, state.position.y);

      this.tweens.add({
        targets: sprite,
        x: pos.x,
        y: pos.y,
        duration: tweenMs,
        ease: 'Quad.easeOut',
      });

      if (label) {
        this.tweens.add({
          targets: label,
          x: pos.x,
          y: pos.y,
          duration: tweenMs,
          ease: 'Quad.easeOut',
        });
      }
    });
  }

  switchCamera(cameraId) {
    this.currentCamera = cameraId;
    EventBus.emit('camera-changed', cameraId);
  }

  fieldRect() {
    const { width, height } = this.scale;
    const mx = 12, my = 6;
    return { x: mx, y: my, w: width - 2 * mx, h: height - 2 * my };
  }

  worldToScreen(worldX, worldY) {
    const vp = this.manifest?.viewport || { width: 100, height: 53.3 };
    const f = this.fieldRect();
    if (this._orientation === 'portrait') {
      // Field +x (downfield 0..vp.width) maps to screen -y (offense
      // moves UP the screen — playbook view). Field +y (across
      // 0..vp.height) maps to screen +x.
      return {
        x: f.x + (worldY / vp.height) * f.w,
        y: f.y + f.h - (worldX / vp.width) * f.h,
      };
    }
    // Legacy landscape — field +x → screen +x, field +y → screen -y.
    return {
      x: f.x + (worldX / vp.width) * f.w,
      y: f.y + f.h - (worldY / vp.height) * f.h,
    };
  }
}
